
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ZoomIn } from 'lucide-react';
import { CiImageOff } from 'react-icons/ci';
import LazyImage from './LazyImage';
import ImageWithFallback from './ImageWithFallback';
import ImageModal from './ImageModal';
import { extractImages } from '../../utils/imageUtils';
import './ThumbnailImage.css';

/**
 * ThumbnailImage - 공통 썸네일 이미지 컴포넌트
 * 
 * 기능:
 * 1. 이미지 표시 (LazyLoading 또는 Fallback 지원)
 * 2. 마우스 호버 시 '돋보기/확대' 아이콘 오버레이 표시
 * 3. 클릭 시 ImageModal 자동 연동 (item 객체 기반 자동 파싱)
 * 4. onClick prop 지원: 외부 핸들러 제공 시 내부 모달 로직을 대체 및 이벤트 전파 제어 가능
 * 
 * @param {Object} item - 제품/재고 데이터 객체 (extractImages로 파싱됨)
 * @param {string} src - (옵션) 직접 이미지 URL 지정 시
 * @param {string} alt - (옵션) alt 텍스트
 * @param {string} className - 추가 CSS 클래스
 * @param {Object} style - 인라인 스타일
 * @param {boolean} useLazy - LazyImage 사용 여부 (기본: true)
 * @param {number} width - 너비 (ImageWithFallback용)
 * @param {number} height - 높이 (ImageWithFallback용)
 * @param {boolean} disabled - 클릭/호버 효과 비활성화 여부
 * @param {function} onClick - (옵션) 클릭 이벤트 핸들러. 제공 시 내부 모달 로직 스킵.
 */
const ThumbnailImage = ({
    item,
    src,
    alt,
    className = '',
    style = {},
    useLazy = true,
    width,
    height,
    disabled = false,
    onClick = null
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    // item이 변경될 때마다 이미지 재추출
    const images = useMemo(() => item ? extractImages(item) : [], [item]);

    // 표시할 이미지 소스 결정
    // 1. props.src가 있으면 최우선
    // 2. item이 있으면 item.thFilePath -> item.FilePath 순
    const displaySrc = src || (item ? (item.thFilePath || item.filePath || item.FilePath || item.FILEPATH) : null);
    const displayAlt = alt || (item ? item.itemNm : '이미지');

    // 모달에 전달할 이미지 리스트 구성
    // item에서 추출된 이미지가 있으면 사용, 없으면 src 또는 displaySrc로 단일 구성
    const modalImages = useMemo(() => {
        if (images.length > 0) return images;
        if (src) return [{ url: src, title: displayAlt, alt: displayAlt }];
        if (displaySrc) return [{ url: displaySrc, title: displayAlt, alt: displayAlt }];
        return [];
    }, [images, src, displaySrc, displayAlt]);

    // 클릭 핸들러
    const handleClick = (e) => {
        if (disabled) return;

        // 외부 핸들러가 있으면 우선 실행하고 내부 모달 로직 스킵
        // 주의: stopPropagation을 하지 않으므로 상위로 이벤트가 전파됨 (원하는 동작)
        if (onClick) {
            onClick(e);
            return;
        }

        e.stopPropagation(); // 부모 클릭 이벤트 전파 방지 (예: 카드 클릭)

        // 이미지가 하나라도 있으면 모달 열기
        if (modalImages.length > 0) {
            setIsModalOpen(true);
        }
    };

    // 모달 닫기
    const handleCloseModal = (e) => {
        if (e) e.stopPropagation();
        setIsModalOpen(false);
    };

    // 표시할 이미지가 없는 경우 처리
    if (!displaySrc && !item) {
        return (
            <div className={`thumbnail-no-image ${className}`} style={style}>
                <CiImageOff size={24} color="#ccc" />
            </div>
        );
    }

    // 오버레이 표시 여부: disabled가 아니고, 이미지가 있고, 모달 이미지가 존재할 때 (onClick 여부와 상관없이 표시)
    const showOverlay = !disabled && displaySrc && modalImages.length > 0;

    return (
        <>
            <div
                className={`thumbnail-image-container ${className} ${disabled ? 'disabled' : ''}`}
                onClick={handleClick}
                style={style}
            >
                {displaySrc && (useLazy ? (
                    <LazyImage
                        src={displaySrc}
                        alt={displayAlt}
                        className="thumbnail-image"
                    />
                ) : (
                    <ImageWithFallback
                        src={displaySrc}
                        alt={displayAlt}
                        className="thumbnail-image"
                        width={width}
                        height={height}
                    />
                ))}

                {showOverlay && (
                    <div className="thumbnail-overlay">
                        <div className="thumbnail-overlay-content">
                            <ZoomIn size={24} color="#ffffff" />
                        </div>
                    </div>
                )}

                {(!displaySrc) && (
                    <div className="thumbnail-no-image-placeholder">
                        <CiImageOff size={30} color="#ccc" />
                    </div>
                )}
            </div>

            {/* 이미지 확대 모달 */}
            {isModalOpen && (
                <ImageModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    images={modalImages}
                    title={displayAlt}
                />
            )}
        </>
    );
};

ThumbnailImage.propTypes = {
    item: PropTypes.object,
    src: PropTypes.string,
    alt: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
    useLazy: PropTypes.bool,
    width: PropTypes.number,
    height: PropTypes.number,
    disabled: PropTypes.bool,
    onClick: PropTypes.func
};

export default ThumbnailImage;
